import { inject, Injectable, OnDestroy, signal } from '@angular/core';

import { DsLoggingService } from '../../shared/services/ds-logging.service';
import { Clock } from '../clock/clock';
import { RuntimeSlideModel } from '../slides/models';
import { Interrupts } from './interrupts';
import { CompletedInterruptModel } from './models';
import { SlideController } from './interfaces';
import { SlideScheduleService } from '../slides/slide-schedule.service';
import { Playlist } from './playlist';
import { InterruptSource, PlaybackState } from './enums';

export function durationMilliseconds(slide: RuntimeSlideModel): number {
  const value = slide.duration.kind === 'media' ? slide.duration.expectedMilliseconds : slide.duration.milliseconds;

  return Number.isFinite(value) && value > 0 ? value : 15_000;
}

// Owns what is on screen and when it changes; a generation per presentation stops a stale one ending the current slide.
@Injectable()
export class PlaybackService implements OnDestroy {
  private readonly clock = inject(Clock);
  private readonly logging = inject(DsLoggingService);
  private readonly schedule = inject(SlideScheduleService);
  private readonly playlist = new Playlist(this.schedule);
  private readonly interruptions = new Interrupts(this.schedule);
  private readonly currentValue = signal<RuntimeSlideModel | null>(null);
  private readonly stateValue = signal<PlaybackState>(PlaybackState.Stopped);
  private readonly positionValue = signal(-1);
  private readonly countValue = signal(0);
  private readonly generationValue = signal(0);
  private readonly presentationsValue = signal(0);
  private readonly completedValue = signal<CompletedInterruptModel | null>(null);
  private presentation?: AbortController;
  private component?: AbortController;
  private activeSource?: InterruptSource;
  private running = false;
  public readonly current = this.currentValue.asReadonly();
  public readonly state = this.stateValue.asReadonly();
  public readonly position = this.positionValue.asReadonly();
  public readonly count = this.countValue.asReadonly();
  public readonly generation = this.generationValue.asReadonly();
  public readonly slidesShownSinceStart = this.presentationsValue.asReadonly();
  public readonly completedInterrupt = this.completedValue.asReadonly();

  public start(slides: readonly RuntimeSlideModel[] = []): void {
    this.stop();
    this.running = true;
    this.show(this.playlist.start(slides, this.now()));
  }

  public stop(): void {
    this.running = false;
    this.cancelPresentation();
    this.interruptions.clear();
    this.playlist.clear();
    this.activeSource = undefined;
    this.currentValue.set(null);
    this.stateValue.set(PlaybackState.Stopped);
    this.positionValue.set(-1);
    this.countValue.set(0);
    this.presentationsValue.set(0);
    this.completedValue.set(null);
  }

  public stage(slides: readonly RuntimeSlideModel[]): void {
    this.playlist.stage(slides);

    if (this.running && !this.current()) {
      this.advance(1);
    }
  }

  public advance(direction: 1 | -1 = 1): void {
    if (!this.running) {
      return;
    }

    this.interruptions.clear();
    this.activeSource = undefined;
    this.show(this.playlist.advance(direction, this.now()));
  }

  public interrupt(slide: RuntimeSlideModel, source: InterruptSource): void {
    if (!this.running) {
      return;
    }

    this.interruptions.put(slide, source);
    this.reconcileInterrupts();
  }

  public resume(source?: InterruptSource): void {
    if (!this.running) {
      return;
    }

    const removed = source || this.activeSource;

    if (removed) {
      this.interruptions.remove(removed);
    }

    this.reconcileInterrupts();
  }

  public checkInterrupts(): void {
    if (this.running) {
      this.reconcileInterrupts();
    }
  }

  // Lets a layout end its own slide (a video finishing, a scroll completing) instead of a fixed timer guessing.
  public attachController(controller: SlideController, slideId: string, generation: number = this.generation()): () => void {
    // A component rendering late may belong to a replaced slide; it gets an inert handle.
    if (!this.running || this.current()?.id !== slideId || generation !== this.generation() || !this.presentation) {
      return (): void => undefined;
    }

    this.component?.abort();
    const component = new AbortController();
    this.component = component;
    const presentation = this.presentation;
    // Chained to the presentation, so ending the slide always tears down the component's work too.
    const abort = (): void => component.abort();
    presentation.signal.addEventListener('abort', abort, { once: true });
    // A detached component can never complete a later presentation, even for the same stable slide ID.
    void Promise.resolve().then((): Promise<void> | void => {
      if (component.signal.aborted) {
        return undefined;
      }

      return controller.present(component.signal);
    }).then(() => {
      if (!component.signal.aborted && generation === this.generation()) {
        this.finish(generation, controller.failed);
      }
    }, () => {
      if (!component.signal.aborted && generation === this.generation()) {
        this.finish(generation, true);
      }
    }).finally(() => { presentation.signal.removeEventListener('abort', abort); });
    return (): void => {
      component.abort();
      presentation.signal.removeEventListener('abort', abort);

      if (this.component === component) {
        this.component = undefined;
      }
    };
  }

  public ngOnDestroy(): void {
    this.stop();
  }

  private reconcileInterrupts(): void {
    const active = this.interruptions.active(this.now());

    if (active) {
      if (this.activeSource !== active.source || this.current()?.id !== active.slide.id) {
        this.activeSource = active.source;
        this.show(active.slide);
      }
    } else if (this.activeSource) {
      this.activeSource = undefined;
      const base = this.playlist.current;
      this.show(base && this.schedule.isEligible(base, this.now()) ? base : this.playlist.advance(1, this.now()));
    }
  }

  private show(slide: RuntimeSlideModel | null): void {
    this.cancelPresentation();
    this.currentValue.set(slide);
    this.positionValue.set(this.playlist.position);
    this.countValue.set(this.playlist.count);
    this.stateValue.set(slide ? (this.activeSource ? PlaybackState.Interrupted : PlaybackState.Playing) : PlaybackState.Waiting);

    if (!slide) {
      return;
    }

    this.presentationsValue.update(value => value + 1);
    const presentation = new AbortController();
    this.presentation = presentation;
    const generation = this.generation();
    const duration = durationMilliseconds(slide);
    // Media ends when its player says so; this is only the deadline for a player that never reports back.
    const safety = Math.max(2 * duration, 90_000);
    const expiry = slide.meta.expiresAt === undefined ? Infinity : Math.max(0, slide.meta.expiresAt - this.now());
    // Whichever comes first: an activity that ends mid-slide is pulled off screen rather than shown out of date.
    const delay = Math.min(slide.duration.kind === 'media' ? safety : duration, expiry);
    this.logging.trackEvent('slide_changed', { currentSlideKind: slide.kind, durationMs: duration });
    void this.clock.after(delay, presentation.signal).then(() => {
      if (generation !== this.generation()) {
        return;
      }

      if (slide.duration.kind === 'media' && delay === safety) {
        this.logging.trackEvent('slide_overrun', { currentSlideKind: slide.kind, durationMs: safety });
      }

      this.finish(generation);
    }, (): void => undefined);
  }

  private finish(generation: number, failed: boolean = false): void {
    // Timer and component can both report the same slide done; the generation stops the second skipping its successor.
    if (!this.running || generation !== this.generation()) {
      return;
    }

    // An interrupt hands the screen back where the playlist left off; a normal slide just moves on.
    if (this.activeSource && this.current()) {
      this.completedValue.set({ id: this.current().id, source: this.activeSource, generation, failed });
      this.resume();
    } else { this.advance(1); }
  }

  private cancelPresentation(): void {
    this.presentation?.abort();
    this.component?.abort();
    this.presentation = undefined;
    this.component = undefined;
    this.generationValue.update(value => value + 1);
  }

  private now(): number {
    return this.clock.now().toEpochMilli();
  }
}
